import type { LockedHoldingCap } from "@repo/shared";
import type { LocalizedString } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import type {
  ProductSuitabilityResult,
  ProductWithSuitabilityMeta,
  SuitabilityContext,
} from "@/lib/services/suitability-service";
import type { RiskModelFilterOptions } from "@/lib/types/goal";
import { T } from "./add-product-dialog.i18n";
import type { CustomProduct } from "./custom-portfolio-builder-types";
import { ProductPickerTable } from "./product-picker-table";

type AddProductDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingProduct: CustomProduct | null;
  selectedProductId: string;
  onSelectProduct: (productId: string) => void;
  amount: string;
  onAmountChange: (amount: string) => void;
  // Units ordering (when the selected product's orderAmountType is "units").
  isUnitsProduct: boolean;
  units: string;
  onUnitsChange: (units: string) => void;
  isSubmitting: boolean;
  onSubmit: () => void;
  // For product picker
  productsWithSuitability: ProductWithSuitabilityMeta[];
  excludeProductIds: string[];
  suitabilityResults: Map<string, ProductSuitabilityResult>;
  suitabilityContext: SuitabilityContext | null;
  clientBypassesSuitability: boolean;
  riskModelOptions: RiskModelFilterOptions | null;
  experiencedProductTypeIds: Set<string>;
  // For totals display
  totalAmount: number;
  // Followup comparison
  isFollowup?: boolean;
  existingAmount?: number | null;
  /** Frozen unit count from the snapshot (units-type products). */
  existingUnits?: number | null;
  /** QL-502 — lock state of the product being edited (null = purchasable). */
  lockedHolding?: LockedHoldingCap | null;
};

export function AddProductDialog({
  open,
  onOpenChange,
  editingProduct,
  selectedProductId,
  onSelectProduct,
  amount,
  onAmountChange,
  isUnitsProduct,
  units,
  onUnitsChange,
  isSubmitting,
  onSubmit,
  productsWithSuitability,
  excludeProductIds,
  suitabilityResults,
  suitabilityContext,
  clientBypassesSuitability,
  riskModelOptions,
  experiencedProductTypeIds,
  totalAmount,
  isFollowup = false,
  existingAmount = null,
  existingUnits = null,
  lockedHolding = null,
}: AddProductDialogProps) {
  const t = useT(T);
  const localize = useLocalize();
  const { formatNumber } = useRegionalFormat();
  // QL-502 — the delta-mode Buy button only needs to know "locked or not";
  // the cap/remove-only distinction is handled below.
  const isLockedProduct = !!lockedHolding?.locked;
  // QP-193 — the selected product's minimum buy (only present when the collection
  // enforces minimums). A positive amount below it blocks saving.
  const activeProductId = editingProduct ? editingProduct.productId : selectedProductId;
  const minBuyAmount =
    productsWithSuitability.find((p) => p.id === activeProductId)?.minBuyAmount ?? null;
  const amountNum = Number.parseFloat(amount || "0");
  // Number, not parseInt: the submission path uses Number(units), and
  // parseInt("1e3") would silently read as 1 while 1000 gets saved.
  const unitsNum = Number(units || "0");

  // Editing a HELD account-followup instrument speaks in instructions — a
  // Sell | Keep | Buy segment plus the trade size — while the parent state
  // keeps carrying the TARGET (existing ± delta), so the submit path and
  // every server rule stay untouched (user design 2026-09-15). New
  // instruments, agreement followups and plain flows keep the target input:
  // there the number IS the buy / the installment, not a delta. Units rows
  // without frozen units fall back too — no base to delta against.
  const deltaMode =
    isFollowup &&
    editingProduct !== null &&
    existingAmount != null &&
    (!isUnitsProduct || existingUnits != null);
  const inUnits = deltaMode && isUnitsProduct && existingUnits != null;
  const existingHolding = inUnits ? (existingUnits as number) : (existingAmount ?? 0);
  const round2 = (n: number) => Math.round(n * 100) / 100;

  const [action, setAction] = useState<"sell" | "keep" | "buy">("keep");
  const [deltaInput, setDeltaInput] = useState("");

  // Derive the instruction from the saved target whenever the dialog opens
  // for a holding (the parent sets amount/units just before opening).
  // Deliberately init-on-open only: amount/units are the values being derived
  // FROM — re-running on their changes would fight the user's input.
  useEffect(() => {
    if (!open || !deltaMode) return;
    const target = inUnits ? Number(units || "0") : Number.parseFloat(amount || "0");
    if (target === existingHolding) {
      setAction("keep");
      setDeltaInput("");
    } else if (target > existingHolding) {
      setAction("buy");
      setDeltaInput(String(round2(target - existingHolding)));
    } else {
      setAction("sell");
      setDeltaInput(String(round2(existingHolding - target)));
    }
  }, [open, editingProduct?.id]);

  /** Set the instruction and push the derived TARGET into the parent state. */
  const applyInstruction = (nextAction: "sell" | "keep" | "buy", nextDelta: string) => {
    setAction(nextAction);
    setDeltaInput(nextDelta);
    const d = inUnits ? Number(nextDelta || "0") : Number.parseFloat(nextDelta || "0");
    const safe = Number.isFinite(d) && d > 0 ? d : 0;
    const target =
      nextAction === "buy"
        ? existingHolding + safe
        : nextAction === "sell"
          ? Math.max(0, existingHolding - safe)
          : existingHolding;
    if (inUnits) onUnitsChange(String(target));
    else onAmountChange(String(round2(target)));
  };

  const deltaNum = inUnits ? Number(deltaInput || "0") : Number.parseFloat(deltaInput || "0");
  const deltaValid =
    deltaInput.trim() !== "" &&
    Number.isFinite(deltaNum) &&
    deltaNum > 0 &&
    (!inUnits || Number.isInteger(deltaNum));
  const sellTooMuch = deltaMode && action === "sell" && deltaValid && deltaNum > existingHolding;
  // QL-398 — min buy is a TRANSACTION rule on an account follow-up: the bank
  // sees the top-up as one purchase, so the top-up itself must meet the
  // product's minimum whatever the resulting holding is. Keep and sell buy
  // nothing and are never judged — the advisor must always be able to return
  // to Keep even when the held amount sits below the minimum. Mirrors the
  // server guard (`assertMinBuySatisfied`).
  const minBuyTopupViolated =
    deltaMode &&
    action === "buy" &&
    !inUnits &&
    minBuyAmount != null &&
    minBuyAmount > 0 &&
    deltaValid &&
    deltaNum < minBuyAmount;

  // Locked products: the cap is the server's (frozen account-follow-up
  // baseline, else 0). Delta mode needs no cap — Buy is disabled outright.
  const lockedCap =
    lockedHolding?.locked && !deltaMode
      ? isUnitsProduct
        ? (lockedHolding.capUnits ?? 0)
        : lockedHolding.capAmount
      : null;
  // Remove-only = locked with no frozen baseline. The table disables the edit
  // button for such rows, so the remove-only branches below (disabled inputs,
  // message, Save disabled) are a guard for the lock state changing while the
  // dialog is open — e.g. a suitability refetch delisting the product.
  const removeOnly = !!lockedHolding?.locked && !lockedHolding.frozen;
  const overLockedCap =
    lockedCap != null && (isUnitsProduct ? unitsNum > lockedCap : amountNum > lockedCap);
  const belowMinBuy =
    !deltaMode &&
    !isUnitsProduct &&
    minBuyAmount != null &&
    minBuyAmount > 0 &&
    amountNum > 0 &&
    amountNum < minBuyAmount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{editingProduct ? t.editProductAmount : t.addProduct}</DialogTitle>
          <DialogDescription>
            {editingProduct ? t.updateAmountDescription : t.searchAndFilterDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!editingProduct && (
            <ProductPickerTable
              productsWithSuitability={productsWithSuitability}
              excludeProductIds={excludeProductIds}
              suitabilityResults={suitabilityResults}
              suitabilityContext={suitabilityContext}
              clientBypassesSuitability={clientBypassesSuitability}
              riskModelOptions={riskModelOptions}
              selectedProductId={selectedProductId}
              onSelectProduct={onSelectProduct}
              defaultProductTypes={experiencedProductTypeIds}
              defaultRiskLevel={suitabilityContext?.goalRiskLevelOrder}
              defaultRiskScore={suitabilityContext?.goalRiskScore}
              defaultTimeHorizon={suitabilityContext?.goalTimeHorizonOrder}
            />
          )}

          {editingProduct && (
            <div className="space-y-2">
              <Label>{t.product}</Label>
              <div className="p-3 border rounded bg-muted/50">
                <p className="font-medium">
                  {localize(editingProduct.product.nameI18n as LocalizedString)}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t.isinLabel} {editingProduct.product.isin}
                </p>
              </div>
            </div>
          )}

          {isFollowup && editingProduct && existingAmount != null && (
            <div className="space-y-1 p-3 border rounded bg-muted/50">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t.currentValue}</span>
                <span className="font-medium">
                  {isUnitsProduct && existingUnits != null
                    ? `${existingUnits} ${t.units.toLowerCase()} (${formatNumber(existingAmount)})`
                    : formatNumber(existingAmount)}
                </span>
              </div>
              {/* Delta mode: the input is the trade, so the feedback is the
                  OUTCOME — what the client holds after the instruction. */}
              {deltaMode ? (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t.newHoldingLabel}</span>
                  <span className="font-medium">
                    {inUnits ? `${unitsNum} ${t.units.toLowerCase()}` : formatNumber(amountNum)}
                  </span>
                </div>
              ) : (
                (isUnitsProduct && existingUnits != null ? unitsNum > 0 : amountNum > 0) &&
                (() => {
                  // Units products with a frozen unit count change in units.
                  const unitBased = isUnitsProduct && existingUnits != null;
                  const delta = unitBased ? unitsNum - existingUnits : amountNum - existingAmount;
                  const label = unitBased
                    ? `${delta > 0 ? "+" : ""}${delta} ${t.units.toLowerCase()}`
                    : `${delta > 0 ? "+" : ""}${formatNumber(delta)}`;
                  return (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t.change}</span>
                      <span
                        className={
                          delta > 0
                            ? "text-success font-medium"
                            : delta < 0
                              ? "text-destructive font-medium"
                              : "text-muted-foreground"
                        }
                      >
                        {label}
                      </span>
                    </div>
                  );
                })()
              )}
            </div>
          )}

          {deltaMode ? (
            <div className="space-y-3">
              {/* Sell | Keep | Buy — ONE segmented switch (2026-10-27): the
                  instruction reads "Sell 5 000", "Buy 2 units". Sell left,
                  Keep as the neutral middle (user ordering 2026-09-15). It
                  sizes to its content, like every other segmented control,
                  so it reads as a switch above the full-width size input
                  rather than as three buttons. Radix reports a re-click of
                  the active item as "" — ignored, so an instruction is
                  always selected and the typed size survives. Locked
                  products cannot be bought — Buy is disabled with the
                  reason, Keep/Sell remain. */}
              <ToggleGroup
                type="single"
                value={action}
                onValueChange={(next) => {
                  if (next === "sell" || next === "keep" || next === "buy") {
                    applyInstruction(next, "");
                  }
                }}
                aria-label={`${t.sell} / ${t.keep} / ${t.buy}`}
              >
                <ToggleGroupItem value="sell" className="min-w-20 data-[state=on]:text-destructive">
                  {t.sell}
                </ToggleGroupItem>
                <ToggleGroupItem value="keep" className="min-w-20">
                  {t.keep}
                </ToggleGroupItem>
                {isLockedProduct ? (
                  // A disabled item gets no hover or focus, so the reason it
                  // is disabled rides on a focusable wrapper (Button's
                  // pattern for disabled tooltips).
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {/* biome-ignore lint/a11y/noNoninteractiveTabindex: focus target for the disabled item's explanation */}
                      <span className="inline-flex cursor-not-allowed" tabIndex={0}>
                        <ToggleGroupItem value="buy" className="min-w-20" disabled>
                          {t.buy}
                        </ToggleGroupItem>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{t.notAvailableForPurchase}</TooltipContent>
                  </Tooltip>
                ) : (
                  <ToggleGroupItem value="buy" className="min-w-20">
                    {t.buy}
                  </ToggleGroupItem>
                )}
              </ToggleGroup>
              {/* The trade-size slot is reserved in every state (label + input
                  + one message line), so the dialog keeps one height while
                  switching Sell / Keep / Buy and Save does not move under
                  the pointer. Under Keep the slot is empty — not a disabled
                  input, which would invite typing. */}
              <div className="min-h-24 space-y-2">
                {action !== "keep" && (
                  <>
                    <Label htmlFor="delta">
                      {action === "buy"
                        ? inUnits
                          ? t.buyUnitsLabel
                          : t.buyAmountLabel
                        : inUnits
                          ? t.sellUnitsLabel
                          : t.sellAmountLabel}
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="delta"
                        type="number"
                        min={inUnits ? 1 : 0.01}
                        step={inUnits ? 1 : 0.01}
                        value={deltaInput}
                        onChange={(e) => applyInstruction(action, e.target.value)}
                        className={sellTooMuch ? "border-destructive" : undefined}
                      />
                      {action === "sell" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => applyInstruction("sell", String(existingHolding))}
                        >
                          {t.sellAllShortcut}
                        </Button>
                      )}
                    </div>
                    <div className="min-h-5 space-y-1">
                      {sellTooMuch && (
                        <p className="text-sm text-destructive">
                          {t.deltaSellTooMuch(
                            inUnits
                              ? `${existingHolding} ${t.units.toLowerCase()}`
                              : formatNumber(existingHolding),
                          )}
                        </p>
                      )}
                      {inUnits && (
                        <p className="text-xs text-muted-foreground">{t.estimatedFromUnits}</p>
                      )}
                      {action === "buy" &&
                        !inUnits &&
                        !minBuyTopupViolated &&
                        minBuyAmount != null &&
                        minBuyAmount > 0 && (
                          <p className="text-xs text-muted-foreground">
                            {t.minBuyTopupHint(formatNumber(minBuyAmount))}
                          </p>
                        )}
                      {minBuyTopupViolated && (
                        <p className="text-sm text-destructive">
                          {t.minBuyTopupError(
                            formatNumber(deltaNum),
                            formatNumber(minBuyAmount ?? 0),
                          )}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : isUnitsProduct ? (
            <div className="space-y-2">
              <Label htmlFor="units">{t.numberOfUnits}</Label>
              <Input
                id="units"
                type="number"
                min="1"
                step="1"
                value={units}
                onChange={(e) => onUnitsChange(e.target.value)}
                placeholder="10"
                className={overLockedCap ? "border-destructive" : undefined}
                disabled={removeOnly}
              />
              {(overLockedCap || removeOnly) && lockedCap != null && (
                <p className="text-sm text-destructive">
                  {removeOnly
                    ? t.lockedRemoveOnly
                    : `${t.notAvailableForPurchase} Max: ${lockedCap} ${t.units.toLowerCase()}`}
                </p>
              )}
              <p className="text-xs text-muted-foreground">{t.estimatedFromUnits}</p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="amount">{t.amount}</Label>
              <Input
                id="amount"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => onAmountChange(e.target.value)}
                placeholder="10000"
                className={overLockedCap ? "border-destructive" : undefined}
                disabled={removeOnly}
              />
              {(overLockedCap || removeOnly) && lockedCap != null && (
                <p className="text-sm text-destructive">
                  {removeOnly
                    ? t.lockedRemoveOnly
                    : `${t.notAvailableForPurchase} Max: ${formatNumber(lockedCap)}`}
                </p>
              )}
              {minBuyAmount != null && minBuyAmount > 0 && (
                <p className="text-xs text-muted-foreground">
                  {t.minBuyDialogHint(formatNumber(minBuyAmount))}
                </p>
              )}
              {belowMinBuy && (
                <p className="text-sm text-destructive">
                  {t.minBuyDialogError(formatNumber(minBuyAmount ?? 0))}
                </p>
              )}
              {totalAmount > 0 && (
                <p className="text-xs text-muted-foreground">
                  {t.currentTotal} {formatNumber(totalAmount)}
                  {editingProduct &&
                    ` | ${t.newTotal} ${formatNumber(totalAmount - editingProduct.amount + parseFloat(amount || "0"))}`}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogCancel>{t.cancel}</DialogCancel>
          <Button
            onClick={onSubmit}
            loading={isSubmitting}
            disabled={
              (!editingProduct && !selectedProductId) ||
              (deltaMode
                ? sellTooMuch || minBuyTopupViolated || (action !== "keep" && !deltaValid)
                : belowMinBuy || overLockedCap || removeOnly)
            }
          >
            {editingProduct ? t.save : t.addProduct}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
